import { AfterViewInit, Directive, ElementRef, inject, NgZone, OnDestroy, Renderer2 } from '@angular/core';
import { colors } from '@improver/design-tokens/ts/colors';

const FIRST_SHAPE_COLOR_VAR_NAME = '--theme-profile-header-gradient-shape-first-background-color';
const SECOND_SHAPE_COLOR_VAR_NAME = '--theme-profile-header-gradient-shape-second-background-color';
const SVG_TEMPLATE = `<svg width="696" height="144" viewBox="0 0 696 144" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g opacity="0.6" clip-path="url(#clip0_27906_100870)">
                                      <path d="M-8.45791 231.625C77.4211 122.858 453.948 -80.1699 602.342 -199L750 -5.23195C556 -49.3918 -115.807 367.584 -8.45791 231.625Z" fill="${FIRST_SHAPE_COLOR_VAR_NAME}"/>
                                      <path d="M-3.94053 257.086C103.349 160.031 490.049 19.4415 672.066 -38.4676L793.734 114.641C611.394 47.6385 -138.052 378.404 -3.94053 257.086Z" fill="${SECOND_SHAPE_COLOR_VAR_NAME}"/>
                                    </g>
                                    <defs>
                                      <clipPath id="clip0_27906_100870">
                                        <rect width="696" height="190" fill="white"/>
                                      </clipPath>
                                    </defs>
                                  </svg>`;

const encodeSvgForUri = (svg: string): string =>
  svg.replace(/"/g, "'").replace(/#/g, '%23').replace(/</g, '%3C').replace(/>/g, '%3E').replace(/\s+/g, ' ').trim();

const replaceSvgColors = (svg: string, primaryColor: string, secondaryColor: string): string =>
  svg
    .replace(new RegExp(FIRST_SHAPE_COLOR_VAR_NAME, 'g'), primaryColor)
    .replace(new RegExp(SECOND_SHAPE_COLOR_VAR_NAME, 'g'), secondaryColor);

@Directive({
  selector: '[lay-header-background]',
  standalone: true,
})
export class LayoutHeaderBackgroundDirective implements AfterViewInit, OnDestroy {
  private readonly elementRef: ElementRef = inject(ElementRef);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly ngZone: NgZone = inject(NgZone);
  private intersectionObserver: IntersectionObserver | null = null;

  ngAfterViewInit(): void {
    this.setupIntersectionObserver();
  }

  ngOnDestroy(): void {
    this.disconnectObserver();
  }

  private setupIntersectionObserver(): void {
    this.ngZone.runOutsideAngular(() => {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            this.ngZone.run(() => {
              this.updateBackgroundImage();

              this.disconnectObserver();
            });
          });
        },
        { threshold: 0.1 }
      );

      this.intersectionObserver.observe(this.elementRef.nativeElement);
    });
  }

  private updateBackgroundImage() {
    const primaryColor = this.getCssVariableValue(FIRST_SHAPE_COLOR_VAR_NAME, colors.primary95);
    const secondaryColor = this.getCssVariableValue(SECOND_SHAPE_COLOR_VAR_NAME, colors.primary110);
    const processedSvg = replaceSvgColors(SVG_TEMPLATE, primaryColor, secondaryColor);

    this.renderer.setStyle(this.elementRef.nativeElement, 'backgroundImage', `url("data:image/svg+xml,${encodeSvgForUri(processedSvg)}")`);
  }

  private getCssVariableValue(variableName: string, defaultValue: string): string {
    const elementStyles = getComputedStyle(this.elementRef.nativeElement);
    let value = elementStyles.getPropertyValue(variableName).trim();

    if (!value) {
      const rootStyles = getComputedStyle(document.documentElement);

      value = rootStyles.getPropertyValue(variableName).trim();
    }

    return value || defaultValue;
  }

  private disconnectObserver(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }
}
